import { formatDateTime } from './utils'

/**
 * Which build this tab is running.
 *
 * The whole point is a support call. "It is not working on my phone" is
 * unanswerable while nothing on screen says which code that phone is holding —
 * and the failure this most often turns out to be is a device stuck on an old
 * build, which looks exactly like a bug in the new one. A seven-character
 * string read out loud settles it in one sentence.
 *
 * Not `package.json`. That file says `"version": "0.0.0"` and nobody has ever
 * bumped it, so anything derived from it would name every build ever made.
 */

/*
 * Read through `typeof` rather than directly.
 *
 * Vite replaces the bare identifier with a string literal, so after
 * substitution these read `typeof "38c8401"` and fold away to nothing. Without
 * the guard, any environment that loads this module without the `define` —
 * a bare `node`, a tool that imports src/ with its own bundler — dies on a
 * ReferenceError at import time, and it would die while rendering the footer
 * of a working application.
 */
export const BUILD_COMMIT = typeof __BUILD_COMMIT__ === 'string' ? __BUILD_COMMIT__ : ''
export const BUILD_TIME = typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : ''

/** What `formatDate` and friends show for a value that is not there. */
const NOTHING = '—'

/**
 * A build identifier for a build with no git behind it.
 *
 * Sliced out of the ISO string rather than run through `Intl`, deliberately.
 * This is an identifier, and one formatted in the reader's time zone is a
 * different string on a phone in Sarajevo and on a laptop in London looking at
 * the same build — which defeats the only thing it is for. `20260917-1340`
 * is UTC, unambiguous, and sorts.
 */
export function stampFrom(iso: string): string {
  const [date, time] = iso.split('T')
  if (!date || !time) return ''

  return `${date.replace(/-/g, '')}-${time.slice(0, 5).replace(':', '')}`
}

/*
 * Both take their parts as defaulted arguments, and every call site passes
 * nothing. It is the only way to assert the git-less shape: the constants are
 * substituted at build time, so inside a test they hold whatever the machine
 * running it happened to have — which is precisely the case that cannot be
 * reproduced by asking for it.
 */

/**
 * The compact form, for the footer — the commit when there is one.
 *
 * The fallback is not decoration. CI and the server both run `npm run build`,
 * and only the server's runs inside a checkout; a build from a tarball still
 * has to be identifiable, or the one case where somebody shipped by hand is
 * the one case nobody can diagnose.
 */
export function shortVersion(commit = BUILD_COMMIT, iso = BUILD_TIME): string {
  return commit || stampFrom(iso) || NOTHING
}

/** Everything at once, for the screen a support call asks somebody to open. */
export function fullVersion(commit = BUILD_COMMIT, iso = BUILD_TIME): string {
  const built = iso ? formatDateTime(iso) : ''

  if (commit && built) return `${commit} · ${built}`

  return commit || built || NOTHING
}
